'use client';

import { useEffect, useState } from 'react';
import { styled } from '@linaria/react';
import Modal from '@fe-news/components/molecules/Modal';
import MemberButton from '@fe-news/components/atoms/Member/MemberButton';
import { SCREEN_SIZE } from '@fe-common-utils/constants/screen';
import { useMutation } from '@tanstack/react-query';
import DragList from './DragList';
import { removeTrackTag } from '@fe-news/api/member';
import useMediaQuery from '@fe-news/hooks/use-media-query';
import useUpdateMyCollection from '@fe-news/hooks/use-update-my-collection';

// type
import { MyCollectionProductType } from '@fe-news/constants/my-track/type';

const Content = styled.div<{ isPopTopic?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  letter-spacing: 0.5px;
  min-width: ${({ isPopTopic }) => (isPopTopic ? '518px' : '368px')};

  @media screen and (max-width: ${SCREEN_SIZE.TABLET}px) {
    min-width: 343px;
  }
`;

const Title = styled.h3`
  font-size: 22px;
  font-weight: 500;
  margin-bottom: 24px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  font-size: 12px;
  color: #848d97;
  padding-left: 10px;
  padding-right: 12px;
  width: 100%;
  border-top: solid 1px #e2e8f1;
  min-height: 30px;
`;

const Delete = styled.p`
  flex: 1;
  margin-right: 12px;
`;

const ToTop = styled.p`
  margin-right: 16px;
`;

const ButtonContainer = styled.div`
  display: flex;
  column-gap: 16px;
  margin-top: 16px;
  letter-spacing: 0.5px;
  width: 100%;
`;

const CancelButtonText = styled.p`
  font-size: 16px;
  color: #e03f19;
`;

const SaveButtonText = styled.p`
  font-size: 16px;
  color: #fff;
`;

type EditModalProps = {
  title: string;
  isPopTopic?: boolean;
  isEditModalClosed: boolean;
  onClickEditModalClose: () => void;
  list: string[];
  onClickRefetch: () => void;
  trackListTitle?: Map<string, string>;
};

const EditModal = ({
  title,
  isPopTopic = false,
  isEditModalClosed,
  onClickEditModalClose,
  list,
  onClickRefetch,
  trackListTitle
}: EditModalProps) => {
  const [data, setData] = useState(list);
  const [deleteTag, setDeleteTag] = useState<string[]>([]);
  const { mutateAsync: removeTagMutate } = useMutation({
    mutationFn: removeTrackTag
  });

  const { putMyCollection } = useUpdateMyCollection({
    refetchMyCollection: onClickRefetch
  });
  const isMobile = useMediaQuery(`(max-width: ${SCREEN_SIZE.TABLET}px)`);

  const onClickSave = async () => {
    const tagIds = deleteTag.join(',');
    const popTopicIds = data.filter(item => !deleteTag.includes(item));

    isPopTopic
      ? await putMyCollection({ productType: MyCollectionProductType.POPTOPIC, data: popTopicIds })
      : await removeTagMutate(tagIds);
    onClickRefetch();
    onClickEditModalClose();
  };

  const onClickDeleteItem = (tag: string) => {
    setDeleteTag([...deleteTag, tag]);
  };

  const onClickSetData = (data: string[]) => {
    setData(data);
  };

  useEffect(() => {
    setData(list);
  }, [list]);

  return (
    <Modal
      isClose={isEditModalClosed}
      onClose={onClickEditModalClose}
      closeButtonStyle={{ top: '6px', right: '6px' }}
      modalContainerStyle={{
        padding: '24px 16px 16px 16px',
        maxWidth: isMobile ? '375px' : isPopTopic ? '550px' : '400px',
        height: '545px'
      }}
    >
      <Content isPopTopic={isPopTopic}>
        <Title>{title}</Title>
        <Header>
          <Delete>刪除</Delete>
          {isPopTopic && (
            <>
              <ToTop>置頂</ToTop>
              <p>排序</p>
            </>
          )}
        </Header>
        <DragList
          title={title}
          isPopTopic={isPopTopic}
          onClickDeleteItem={onClickDeleteItem}
          list={data}
          trackListTitle={trackListTitle}
          onClickSetData={onClickSetData}
        />
        <ButtonContainer>
          <MemberButton onClick={onClickEditModalClose} isHollowType>
            <CancelButtonText>取消</CancelButtonText>
          </MemberButton>
          <MemberButton onClick={onClickSave}>
            <SaveButtonText>儲存</SaveButtonText>
          </MemberButton>
        </ButtonContainer>
      </Content>
    </Modal>
  );
};

export default EditModal;
